import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import InsuranceAttributeCard from "@/components/InsuranceAttributeCard";
import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import RuleDiffAttributeCard from "@/components/RuleDiffAttributeCard";
import {
  getPerStateInsurance,
  INSURANCE_ATTR_DISPLAY_ORDER,
} from "@/data/per-state-insurance";
import {
  ApiError,
} from "@/lib/api";
import { getProfile } from "@/lib/profile-api";
import {
  BROWSE_COUNTRIES,
  isoToBrowseCode,
} from "@/lib/rulediff-browse-countries";
import {
  browseStatesByCategory,
  getBrowseState,
} from "@/lib/rulediff-browse-states";
import {
  ATTR_DISPLAY_ORDER,
  type RuleDiffSeedEntry,
  getSeedRuleDiff,
} from "@/lib/rulediff-seed-api";

/**
 * apps/web :: pages :: RuleDiffBrowsePage
 *
 * Authenticated-only browse surface for the precomputed rule-diff seed
 * corpus (1,737 country-state JSONs from Wave 17b/17c). Calls the
 * Wave 17d PR-A backend (`GET /api/rule-diff/seed/{country}/{state}`).
 *
 * Distinct from `RuleDiffPreviewPage` (the LLM-generate playground):
 *   - Browse: country/state picker → render 10 structured attribute
 *     cards instantly. Zero LLM cost. Severity-tagged colors.
 *   - Preview: free-text form → LLM-generated prose diff. Costs
 *     tokens; ~2-5 sec latency.
 *
 * Auto-populates from the user's profile when available:
 *   - Country prefilled from `profile.home_country` (ISO code) via
 *     {@link isoToBrowseCode}; falls back to "mx" if unmapped.
 *   - Destination state prefilled from `profile.target_states[0]`;
 *     falls back to "CA".
 *
 * Load-state machine mirrors the established DashboardPage pattern:
 * loading → ready / not-found / error. 404 from the seed endpoint is a
 * legitimate "not yet supported pair" outcome (e.g., a country/state
 * outside the corpus) and is rendered as a warm empty state, not an
 * error toast.
 */

type LoadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; entries: RuleDiffSeedEntry[] }
  | { kind: "not-found" }
  | { kind: "error"; message: string };

export default function RuleDiffBrowsePage() {
  const { t } = useTranslation();
  const stateGroups = useMemo(() => browseStatesByCategory(), []);

  const [country, setCountry] = useState<string>("mx");
  const [state, setState] = useState<string>("ca");
  const [loadState, setLoadState] = useState<LoadState>({ kind: "idle" });

  // Profile auto-populate. Best-effort; swallow failures silently
  // because the page must still work for users without a profile yet.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const profile = await getProfile();
        if (cancelled || !profile) return;
        const cc = isoToBrowseCode(profile.home_country);
        if (cc) setCountry(cc);
        const st = profile.target_states?.[0];
        // Profile target_states are UsStateCode (uppercase). Lowercase
        // for the seed-corpus filename convention; verify catalog
        // membership before assigning so an unknown code doesn't break
        // the picker.
        if (st && getBrowseState(st)) setState(st.toLowerCase());
      } catch {
        // Silently ignored — the picker defaults are still usable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch seed whenever the picker changes. Cancellation guard for
  // out-of-order responses if the user changes selection mid-flight.
  useEffect(() => {
    let cancelled = false;
    setLoadState({ kind: "loading" });
    void (async () => {
      try {
        const entries = await getSeedRuleDiff(country, state);
        if (cancelled) return;
        // Sort entries into the canonical display order.
        const ordered = [...entries].sort(
          (a, b) =>
            ATTR_DISPLAY_ORDER.indexOf(a.attr_name) -
            ATTR_DISPLAY_ORDER.indexOf(b.attr_name),
        );
        setLoadState({ kind: "ready", entries: ordered });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setLoadState({ kind: "not-found" });
          return;
        }
        const message =
          err instanceof Error ? err.message : t("ruleDiffBrowse.errorGeneric");
        setLoadState({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [country, state, t]);

  return (
    <LearnerPortalShell pageTitle={t("ruleDiffBrowse.title")}>
      <section className="mx-auto max-w-4xl px-md pt-xl pb-xl sm:px-lg sm:pt-2xl sm:pb-2xl">
        <header className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-wide text-neutral-600">
          {t("ruleDiffBrowse.eyebrow")}
        </p>
        <h1 className="mt-sm font-display text-3xl font-bold tracking-tight text-brand-primary sm:text-4xl">
          {t("ruleDiffBrowse.title")}
        </h1>
        <div
          className="mt-md h-1 w-16 rounded-full bg-brand-accent"
          aria-hidden="true"
        />
        <p className="mt-md text-base text-neutral-800">
          {t("ruleDiffBrowse.subtitle")}
        </p>
      </header>

      <fieldset
        className="mt-xl rounded-md border border-neutral-200 bg-white p-md sm:p-lg"
      >
        <legend className="px-sm text-sm font-semibold text-neutral-800">
          {t("ruleDiffBrowse.pickerLegend")}
        </legend>
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
          <label className="flex flex-col gap-xs">
            <span className="text-sm font-medium text-neutral-800">
              {t("ruleDiffBrowse.countryLabel")}
            </span>
            <select
              data-testid="rule-diff-browse-country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="rounded-md border border-neutral-300 bg-white px-sm py-xs text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {BROWSE_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                  {c.isLhs ? ` ${t("ruleDiffBrowse.lhsTag")}` : ""}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-xs">
            <span className="text-sm font-medium text-neutral-800">
              {t("ruleDiffBrowse.stateLabel")}
            </span>
            <select
              data-testid="rule-diff-browse-state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="rounded-md border border-neutral-300 bg-white px-sm py-xs text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              <optgroup label={t("ruleDiffBrowse.stateGroup.states")}>
                {stateGroups.states.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label={t("ruleDiffBrowse.stateGroup.district")}>
                {stateGroups.district.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label={t("ruleDiffBrowse.stateGroup.territories")}>
                {stateGroups.territories.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                    {s.isLhs ? ` ${t("ruleDiffBrowse.lhsTag")}` : ""}
                  </option>
                ))}
              </optgroup>
            </select>
          </label>
        </div>
      </fieldset>

      <div className="mt-xl" data-testid="rule-diff-browse-results">
        {loadState.kind === "loading" && (
          <p
            role="status"
            aria-live="polite"
            className="text-sm text-neutral-600"
          >
            {t("ruleDiffBrowse.loading")}
          </p>
        )}
        {loadState.kind === "not-found" && (
          <div
            role="status"
            className="rounded-md border border-amber-300 bg-amber-50 p-md text-sm text-amber-900"
          >
            <p className="font-semibold">
              {t("ruleDiffBrowse.notFoundTitle")}
            </p>
            <p className="mt-xs">{t("ruleDiffBrowse.notFoundBody")}</p>
          </div>
        )}
        {loadState.kind === "error" && (
          <div
            role="alert"
            className="rounded-md border border-rose-300 bg-rose-50 p-md text-sm text-rose-900"
          >
            <p className="font-semibold">{t("ruleDiffBrowse.errorTitle")}</p>
            <p className="mt-xs">{loadState.message}</p>
          </div>
        )}
        {loadState.kind === "ready" && (
          <ul
            className="flex flex-col gap-md"
            aria-label={t("ruleDiffBrowse.resultsAriaLabel")}
          >
            {loadState.entries.map((entry) => (
              <li key={entry.attr_name}>
                <RuleDiffAttributeCard entry={entry} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {/*
        Insurance section. Renders for all 50 + DC; surfaces a "not
        available" notice for territories. Independent of the rule-diff
        load state — insurance content is a per-state lookup, not a
        country×state pair, so we can render it as long as the user has
        a state selected.
      */}
      <InsuranceSection stateCode={state} />
      </section>
    </LearnerPortalShell>
  );
}

interface InsuranceSectionProps {
  /** Lower-case 2-letter state/territory code from the picker. */
  readonly stateCode: string;
}

/**
 * Renders the per-state insurance attribute cards as a sibling section
 * to the rule-diff cards. Unlike the rule-diff cards (one per country
 * pair), insurance content is a state-only lookup — no diff, just the
 * state's published minimums.
 *
 * Layout: section heading + 6 cards (one per insurance attribute,
 * displayed in INSURANCE_ATTR_DISPLAY_ORDER). The 7th attribute
 * (penalty text) renders as a callout block below the grid because
 * it's a paragraph, not a value/citation pair.
 */
function InsuranceSection({ stateCode }: InsuranceSectionProps) {
  const { t } = useTranslation();
  const entry = getPerStateInsurance(stateCode);

  if (!entry) {
    return (
      <section
        aria-labelledby="rule-diff-insurance-section-heading"
        className="mt-2xl"
      >
        <h2
          id="rule-diff-insurance-section-heading"
          className="font-display text-2xl font-bold tracking-tight text-brand-primary sm:text-3xl"
        >
          {t("ruleDiffBrowse.insurance.sectionHeading")}
        </h2>
        <div
          role="status"
          className="mt-md rounded-md border border-amber-300 bg-amber-50 p-md text-sm text-amber-900"
        >
          <p className="font-semibold">
            {t("ruleDiffBrowse.insurance.noEntryTitle")}
          </p>
          <p className="mt-xs">
            {t("ruleDiffBrowse.insurance.noEntryBody")}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="rule-diff-insurance-section-heading"
      className="mt-2xl"
      data-testid="rule-diff-insurance-section"
    >
      <h2
        id="rule-diff-insurance-section-heading"
        className="font-display text-2xl font-bold tracking-tight text-brand-primary sm:text-3xl"
      >
        {t("ruleDiffBrowse.insurance.sectionHeading")}
      </h2>
      <p className="mt-sm text-base text-neutral-800">
        {t("ruleDiffBrowse.insurance.sectionSubtitle")}
      </p>
      <ul
        className="mt-lg flex flex-col gap-md"
        aria-label={t("ruleDiffBrowse.insurance.sectionAriaLabel")}
      >
        {INSURANCE_ATTR_DISPLAY_ORDER.map((attr) => (
          <li key={attr}>
            <InsuranceAttributeCard entry={entry} attr={attr} />
          </li>
        ))}
      </ul>

      {/*
        Penalty text block. Rendered as a callout, not a card, because
        the content is a multi-sentence paragraph rather than a single
        value. Citation footer mirrors the cards above for consistency.
      */}
      <div
        className="mt-md rounded-md border-l-4 border-rose-300 bg-rose-50/40 p-md sm:p-lg"
        data-testid="rule-diff-insurance-penalty"
      >
        <h3
          id="rule-diff-insurance-penalty-heading"
          className="font-display text-lg font-semibold text-brand-primary"
        >
          {/*
            We reuse the attr-name as the heading so the i18n key
            structure stays simple. The penalty text itself is a
            LocalizedString.
          */}
          {/* No dedicated heading translation — the penalty text is
              self-describing. Use a brief generic heading. */}
          {t("ruleDiffBrowse.insurance.attr.uninsuredMotorist")}
        </h3>
        <p className="mt-sm text-sm text-neutral-800">
          {/*
            Penalty text is a LocalizedString; pull en/es by
            current i18n language. Falls back to en for any locale
            we haven't shipped yet.
          */}
          <PenaltyParagraph entry={entry} />
        </p>
        <footer className="mt-md flex flex-wrap items-center gap-md text-xs text-neutral-600">
          <span>
            {t("ruleDiffBrowse.insurance.retrievedLabel")}:{" "}
            {entry.uninsuredPenaltyCitation.retrievedOn}
          </span>
          <a
            href={entry.uninsuredPenaltyCitation.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            {t("ruleDiffBrowse.sourceLink")}
            <span className="sr-only"> {t("ruleDiffBrowse.opensInNewTab")}</span>
          </a>
        </footer>
      </div>
    </section>
  );
}

interface PenaltyParagraphProps {
  readonly entry: ReturnType<typeof getPerStateInsurance>;
}

function PenaltyParagraph({ entry }: PenaltyParagraphProps) {
  const { i18n } = useTranslation();
  if (!entry) return null;
  const text = i18n.language?.startsWith("es")
    ? entry.uninsuredPenaltyText.es
    : entry.uninsuredPenaltyText.en;
  return <>{text}</>;
}
